import { Component, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MapBoxProvider } from 'leaflet-geosearch';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { LP_UI_CONFIG } from '../../core/tokens';
import { Sensor } from '../../models/classes/sensor';
import { AssetsService } from '../../services/asset.service';
import { I18nService } from '../../services/i18n.service';
import { UiService } from '../../services/ui.service';
import { NavButtonComponent } from '../nav-button/nav-button';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { FormsModule } from '@angular/forms';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { MapComponent } from '../map/map';
import { sanitizeUnicodeText } from '../../utilities/global.util';
import { GLOBAL_CONSTANT } from '../../constants/global.constant';

@Component({
  selector: 'app-embedded-sensor-map',
  templateUrl: './embedded-sensor-map.html',
  styleUrls: ['./embedded-sensor-map.scss'],
  imports: [
    NavButtonComponent,
    NzAutocompleteModule,
    NzSwitchModule,
    FormsModule,
    NzInputModule,
    NzIconModule,
    MapComponent,
  ],
})
export class EmbeddedSensorMapComponent implements OnInit {
  private readonly cfg = inject(LP_UI_CONFIG);
  private ui = inject(UiService);
  private assetsService = inject(AssetsService);
  public router = inject(Router);
  public i18n = inject(I18nService);

  startLocation = input<{ lat: number; lng: number } | null>(null);

  positionChanged = output<{ lat: number; lng: number }>();
  exit = output<void>();

  showSensors = signal(false);
  satelliteView = signal(true);
  sensors = signal<Sensor[]>([]);
  searchString = signal(GLOBAL_CONSTANT.EMPTY_STRING);
  showSensorPosition = signal(true);
  userLocation = signal<{ lat: number; lng: number } | null>(null);
  sensorPosition = signal<{ lat: number; lng: number } | null>(null);
  searchResults = signal<any[]>([]);

  isMobile = computed(() => this.ui.IsMobile());
  isDesktop = computed(() => this.ui.IsDesktop());

  searchStringUpdate$ = new Subject<any>();
  private searchProvider: any;
  private subscriptions = new Subscription();
  sensorSub: any;

  ngOnInit() {
    this.initGeolocation();
    this.initSearchProvider();
    this.initSearchSubscription();
    this.initSensorSubscription();
  }

  private initGeolocation() {
    if (!this.startLocation() && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.userLocation.set({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      });
    }
  }

  async initSearchProvider() {
    const keys = await this.assetsService.getMapBoxKeys(); // Ensure service returns a Promise
    this.searchProvider = new MapBoxProvider({
      params: {
        access_token: keys.geoCodingToken,
        language: this.i18n.languageSignal(), // user language
      },
    });
    // this.searchProvider = new MapBoxProvider({
    //   params: {
    //     access_token: this.cfg.mapbox.geoCodingToken,
    //     language: this.i18n.languageSignal(),
    //   },
    // });
  }

  private initSearchSubscription() {
    const searchSub = this.searchStringUpdate$
      .pipe(
        tap((value) => {
          if (value?.x) {
            this.showSensorPosition.set(true);
            const pos = { lat: value.y, lng: value.x };
            this.sensorPosition.set(pos);
            this.positionChanged.emit(pos);
          }
        }),
        debounceTime(300)
      )
      .subscribe((value) => {
        if (value === GLOBAL_CONSTANT.EMPTY_STRING) {
          this.showSensorPosition.set(true);
        } else if (value && !value.x) {
          this.performSearch(value);
        }
      });
    this.subscriptions.add(searchSub);
  }

  private async performSearch(query: string) {
    const res = await this.searchProvider.search({ query });
    this.showSensorPosition.set(false);

    const unique = [...new Map(res.map((item: any) => [item.label, item])).values()] as any[];

    const processed = unique.map((r) => {
      const idx = r.label.indexOf(',');

      const label =
        idx > GLOBAL_CONSTANT.DEFAULT_INITIALIZER
          ? r.label.substring(GLOBAL_CONSTANT.DEFAULT_INITIALIZER, idx)
          : r.label;

      const description =
        idx > GLOBAL_CONSTANT.DEFAULT_INITIALIZER
          ? r.label.substring(idx + 1)
          : GLOBAL_CONSTANT.EMPTY_STRING;

      const lowerLabel = label.toLowerCase();
      const lowerQuery = query.toLowerCase();

      const matchIndex = lowerLabel.indexOf(lowerQuery);

      const shortenedLabel =
        matchIndex >= 0
          ? label.substring(0, matchIndex) + label.substring(matchIndex + query.length)
          : label;

      return {
        label: shortenedLabel,
        searchTerm: matchIndex >= 0 ? query : GLOBAL_CONSTANT.EMPTY_STRING,
        description,
        value: r,
        fulltext: r.label,
      };
    });

    this.searchResults.set(processed);
  }

  async initSensorSubscription() {
    const sensorSub: any = await this.assetsService.getSensors();
    this.sensors.set(sensorSub);
    this.subscriptions.add(sensorSub);
  }

  onPositionChanged(event: { lat: number; lng: number }) {
    this.positionChanged.emit(event);
  }

  onInputFocus() {
    this.showSensorPosition.set(false);
  }

  onInputBlur() {
    this.showSensorPosition.set(true);
  }

  onTextInput(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    const cleaned = sanitizeUnicodeText(inputEl.value);

    if (cleaned !== inputEl.value) {
      inputEl.value = cleaned; // update the DOM value
    }
    //sync to model:
    this.searchString.set(cleaned);
  }
}
